
import { getSportIcon } from './sportsUtils';
import { getTeamLogoFallback } from './teamLogos';

/**
 * Normalizes and enriches a raw event from the backend.
 * Applies heuristics to detect sport types, formats team names, and prepares data for display.
 * This should be run ONCE when data is fetched, not during render.
 */
export const processEvent = (event) => {
    if (!event) return null;

    // 1. Basic Extraction
    const homeCompetitor = event.competitors?.find(c => c.qualifier === 'home') || event.competitors?.[0];
    const awayCompetitor = event.competitors?.find(c => c.qualifier === 'away') || event.competitors?.[1];

    const homeTeam = event.team1 || homeCompetitor?.name || 'Local';
    const awayTeam = event.team2 || awayCompetitor?.name || 'Visita';

    // 2. Score Formatting
    const s1 = event.home_score ?? 0;
    const s2 = event.away_score ?? 0;
    const formattedScore = (s1 === 0 && s2 === 0) ? null : `${s1} - ${s2}`;

    // 3. Sport Detection Heuristics
    // Use fallback to '1' (Soccer) only if truly unknown
    let sportId = Number(event.sport_id || event.sportId || event.sport_key || (event.sport && event.sport.id) || 1);

    // Explicit Spanish Names for "TODOS" view variety
    let sportName = 'Fútbol'; // Default

    // Heuristics based on event data
    const leagueRaw = (event.league || event.tournament?.name || "").toLowerCase();
    const homeRaw = homeTeam.toLowerCase();
    const awayRaw = awayTeam.toLowerCase();

    const hasCommaFormat = homeRaw.includes(',') || awayRaw.includes(',');
    const isTennisContext =
        leagueRaw.includes('singles') ||
        leagueRaw.includes('doubles') ||
        leagueRaw.includes('utr') ||
        leagueRaw.includes('atp') ||
        leagueRaw.includes('wta') ||
        leagueRaw.includes('itf') ||
        leagueRaw.includes('challenger') ||
        leagueRaw.includes('invitational') ||
        hasCommaFormat;

    // Market checks for Draw (X)
    const mainMarket = event.markets?.[0];
    const hasDraw = mainMarket?.outcomes?.some(o => {
        const n = (o.name || "").toLowerCase();
        return n === 'x' || n === 'draw' || n === 'empate' || String(o.id) === 'x';
    });

    // Apply Corrections
    if (isTennisContext) {
        sportId = 2; // Tennis
        sportName = 'Tenis';
    } else if (sportId === 1 || !sportId) {
        // If marked as soccer or unknown but has tennis traits
        if (!hasDraw && mainMarket) {
            // suspicious
        }
    }

    // Force Name and ID alignment based on strings (Robustness)
    const rawSportName = (event.sport_name || "").toLowerCase();
    if (rawSportName.includes('basket') || rawSportName.includes('baloncesto')) {
        sportId = 3;
        sportName = 'Baloncesto';
    }
    else if (rawSportName.includes('baseball') || rawSportName.includes('béisbol')) {
        sportId = 5;
        sportName = 'Béisbol';
    }
    else if (rawSportName.includes('tennis') || rawSportName.includes('tenis')) {
        sportId = 2;
        sportName = 'Tenis';
    }
    else if (rawSportName.includes('hockey') || rawSportName.includes('hielo')) {
        sportId = 4;
        sportName = 'Hockey';
    }
    else if (rawSportName.includes('football') || rawSportName.includes('americano')) {
        sportId = 6;
        sportName = 'Fútbol Americano';
    }
    else if (rawSportName.includes('volleyball') || rawSportName.includes('voleibol')) {
        sportId = 7;
        sportName = 'Voleibol';
    }
    else if (sportId === 1) {
        sportName = 'Fútbol';
    }

    // 4. Status normalization
    // Fix: "1" -> "EN VIVO"
    let rawStatus = String(event.status || 'LIVE');
    let displayStatus = 'EN VIVO';

    // If status is a number (1, 2, 45, 90) or standard codes, show "EN VIVO"
    // Unless it is 'HT' (Half Time) or specific text
    if (rawStatus === 'HT') displayStatus = 'Entretiempo';
    else if (['1', '2', "1'", "2'", 'LIVE'].includes(rawStatus) || !isNaN(parseInt(rawStatus))) {
        displayStatus = 'EN VIVO';
    } else {
        displayStatus = rawStatus === 'Not Started' ? 'Por Comenzar' : rawStatus;
    }

    return {
        ...event,
        // Standardized fields
        id: event.id,
        sportId,
        sportName,
        team1: homeTeam,
        team2: awayTeam,
        team1Logo: homeCompetitor?.logo || event.team1Logo || getTeamLogoFallback(homeTeam),
        team2Logo: awayCompetitor?.logo || event.team2Logo || getTeamLogoFallback(awayTeam),
        score: formattedScore,
        league: event.tournament?.name || event.league || 'Torneo',
        isLive: true,
        displayStatus,
        hasDraw
    };
};

/**
 * Filter events based on active category/sport
 */
export const filterEventsBySport = (events, activeSportId) => {
    if (!events || events.length === 0) return [];

    // "Todos" (All) - Smart Filter for Popular/Main Events
    if (activeSportId === 'all') {
        const priorityLeagues = [
            'champions', 'premier', 'laliga', 'serie a', 'bundesliga', 'ligue 1',
            'libertadores', 'sudamericana', 'nba', 'euroleague', 'mlb', 'nhl',
            'nfl', 'atp', 'wta', 'grand slam', 'roland garros', 'wimbledon', 'australian open', 'us open'
        ];

        const lowTierKeywords = [
            'reserve', 'u19', 'u20', 'u21', 'u23', 'amateur', 'regional',
            'division 2', 'division 3', 'lower', 'women', 'itf', 'challenger',
            'legends', 'esoccer', 'e-soccer', 'simulated', 'cyber', 'virtual', 'electronic', 'battleground', 'gt sports'
        ];

        return events.filter(e => {
            const league = (e.league || "").toLowerCase();
            const sportId = Number(e.sportId);

            // 1. Is it a PRIORITY League? -> SHOW IT (Overrides low tier check if needed)
            if (priorityLeagues.some(pl => league.includes(pl))) {
                return true;
            }

            // 2. Is it a LOW TIER League? -> HIDE IT
            if (lowTierKeywords.some(lt => league.includes(lt))) {
                return false;
            }

            // 3. SHOW VARIETY: Ensure we include main sports.
            // Explicitly allow: Soccer (1), Tennis (2), Basketball (3), Baseball (5), Am. Football (6), Volleyball (7)
            const isMainSport = [1, 2, 3, 5, 6, 7].includes(sportId);

            return isMainSport;
        });
    }

    const targetId = Number(activeSportId);

    return events.filter(e => {
        // Strict filtering based on processed sportId
        if (targetId === 1) { // Soccer
            if (e.sportId !== 1) return false;
            if (!e.hasDraw && e.markets?.[0]) return false;
        }
        else if (targetId === 3) { // Basketball
            if (e.sportId === 2) return false;
            if (e.sportId === 5) return false;
            return e.sportId === 3;
        }
        else if (targetId === 2) { // Tennis
            return e.sportId === 2;
        }

        return e.sportId === targetId;
    });
};
